import { useEffect, useState } from "react";
import type {
  FeedCommentItem,
  FeedListItem,
  FeedReactionKind,
} from "../../../shared/contracts/product-pages.js";
import { toast } from "@/components/ui/sonner";
import {
  addFeedComment,
  deleteFeedComment,
  listFeedComments,
  setFeedReaction,
} from "../../api/feed-api.js";
import { timeAgo } from "../../lib/format.js";
import { CommentSafety } from "../safety/comment-safety.js";

const reactionKinds: FeedReactionKind[] = [
  "fire",
  "clap",
  "trophy",
  "flag",
  "laugh",
];

const reactionLabels: Record<FeedReactionKind, string> = {
  fire: "Fire",
  clap: "Applause",
  trophy: "Trophy",
  flag: "Flag",
  laugh: "Laugh",
};

/** House-style line icons (24 viewBox, stroked with currentColor). */
const iconPaths: Record<FeedReactionKind | "comment" | "close", string> = {
  fire: "M12 3c1 3 4 4.5 4 8.5a4 4 0 0 1-8 0c0-1.5.5-2.5 1.5-3.5.2 1.2.8 2 1.5 2.5C11.5 8 11 5.5 12 3z",
  clap: "M8 12V6.5a1.5 1.5 0 0 1 3 0V11M11 10V4.5a1.5 1.5 0 0 1 3 0V11M14 10.5V6a1.5 1.5 0 0 1 3 0v6.5M17 13v-1a1.5 1.5 0 0 1 3 0v4a6 6 0 0 1-6 6h-1.5a6 6 0 0 1-5.2-3l-2.7-4.6a1.5 1.5 0 0 1 2.4-1.8L8 15M4 6l1.6 1.2M20 3l-1.6 1.4",
  trophy:
    "M8 4h8v5a4 4 0 0 1-8 0zM8 6H5a3 3 0 0 0 3 3M16 6h3a3 3 0 0 1-3 3M12 13v4M9 20h6M10 17h4v3h-4z",
  flag: "M5 21V4M5 4h12l-3 4 3 4H5",
  laugh:
    "M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18M8 13.5a4 4 0 0 0 8 0zM9 9.5h.01M15 9.5h.01",
  comment: "M4 5h16v11H9l-5 3z",
  close: "M6 6l12 12M18 6L6 18",
};

interface SocialState {
  reactions: Record<string, number>;
  viewerReactions: string[];
  commentCount: number;
}

/**
 * Reaction bar, comment toggle and expandable comment thread for one feed
 * card. `actions` renders in the footer row next to the comment toggle.
 */
export function FeedSocial(props: {
  event: FeedListItem;
  actions?: React.ReactNode;
}): React.JSX.Element {
  const { event } = props;
  const [seed, setSeed] = useState(event);
  const [state, setState] = useState<SocialState>(() => socialStateOf(event));
  const [open, setOpen] = useState(false);
  // A refreshed feed payload hands in a new event object; adopt its counts
  // so a rival's reaction or comment shows without a full page reload.
  if (seed !== event) {
    setSeed(event);
    setState(socialStateOf(event));
  }

  const toggleReaction = (kind: FeedReactionKind): void => {
    const on = !state.viewerReactions.includes(kind);
    setState((current) => applyReaction(current, kind, on));
    setFeedReaction(event.sourceType, event.sourceId, kind, on).catch(
      (error: unknown) => {
        setState((current) => applyReaction(current, kind, !on));
        toast(messageOf(error));
      },
    );
  };

  return (
    <>
      <div className="feed-reactbar" role="group" aria-label="Reactions">
        {reactionKinds.map((kind) => (
          <ReactionChip
            key={kind}
            kind={kind}
            count={state.reactions[kind] ?? 0}
            on={state.viewerReactions.includes(kind)}
            onToggle={() => toggleReaction(kind)}
          />
        ))}
      </div>
      <div className="feed-foot2">
        <div className="feed-foot-l">
          <button
            type="button"
            className="feed-comment-toggle"
            aria-expanded={open}
            aria-label={`${open ? "Hide" : "Show"} comments`}
            onClick={() => setOpen((current) => !current)}
          >
            <SocialIcon name="comment" />
            <span>{state.commentCount}</span>
          </button>
        </div>
        {props.actions}
      </div>
      {open ? (
        <CommentThread
          event={event}
          onCountChange={(delta) =>
            setState((current) => ({
              ...current,
              commentCount: Math.max(0, current.commentCount + delta),
            }))
          }
        />
      ) : null}
    </>
  );
}

function socialStateOf(event: FeedListItem): SocialState {
  return {
    reactions: event.reactions ?? {},
    viewerReactions: event.viewerReactions ?? [],
    commentCount: event.commentCount ?? 0,
  };
}

function applyReaction(
  state: SocialState,
  kind: FeedReactionKind,
  on: boolean,
): SocialState {
  const has = state.viewerReactions.includes(kind);
  if (has === on) return state;
  const count = Math.max(0, (state.reactions[kind] ?? 0) + (on ? 1 : -1));
  return {
    ...state,
    reactions: { ...state.reactions, [kind]: count },
    viewerReactions: on
      ? [...state.viewerReactions, kind]
      : state.viewerReactions.filter((item) => item !== kind),
  };
}

function ReactionChip(props: {
  kind: FeedReactionKind;
  count: number;
  on: boolean;
  onToggle(): void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="feed-react-chip"
      data-on={props.on ? "true" : "false"}
      aria-pressed={props.on}
      aria-label={`${reactionLabels[props.kind]} (${props.count})`}
      title={reactionLabels[props.kind]}
      onClick={props.onToggle}
    >
      <SocialIcon name={props.kind} />
      {props.count > 0 ? <span>{props.count}</span> : null}
    </button>
  );
}

function CommentThread(props: {
  event: FeedListItem;
  onCountChange(delta: number): void;
}): React.JSX.Element {
  const { sourceType, sourceId } = props.event;
  const [comments, setComments] = useState<FeedCommentItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listFeedComments(sourceType, sourceId)
      .then((rows) => {
        if (!cancelled) setComments(rows);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(messageOf(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [sourceType, sourceId]);

  const submit = async (): Promise<void> => {
    const body = draft.trim();
    if (body.length === 0 || busy) return;
    setBusy(true);
    try {
      const comment = await addFeedComment(sourceType, sourceId, body);
      setComments((current) => [...(current ?? []), comment]);
      props.onCountChange(1);
      setDraft("");
    } catch (cause) {
      toast(messageOf(cause));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (comment: FeedCommentItem): Promise<void> => {
    try {
      await deleteFeedComment(comment.id);
      setComments((current) =>
        (current ?? []).filter((item) => item.id !== comment.id),
      );
      props.onCountChange(-1);
    } catch (cause) {
      toast(messageOf(cause));
    }
  };

  return (
    <div className="feed-thread">
      <CommentList comments={comments} error={error} onDelete={remove} />
      <CommentComposer
        draft={draft}
        busy={busy}
        onChange={setDraft}
        onSubmit={submit}
      />
    </div>
  );
}

function CommentComposer(props: {
  draft: string;
  busy: boolean;
  onChange(value: string): void;
  onSubmit(): Promise<void>;
}): React.JSX.Element {
  return (
    <form
      className="feed-comment-form"
      onSubmit={(formEvent) => {
        formEvent.preventDefault();
        void props.onSubmit();
      }}
    >
      <textarea
        className="fr-textarea feed-comment-input"
        value={props.draft}
        rows={2}
        maxLength={500}
        placeholder="Add a comment"
        aria-label="Comment"
        onChange={(change) => props.onChange(change.target.value)}
      />
      <div className="feed-comment-actions">
        <span className="feed-comment-count">{props.draft.length}/500</span>
        <button
          type="submit"
          className="fr-btn"
          data-variant="gold"
          disabled={props.busy || props.draft.trim().length === 0}
        >
          Post
        </button>
      </div>
    </form>
  );
}

function CommentList(props: {
  comments: FeedCommentItem[] | null;
  error: string | null;
  onDelete(comment: FeedCommentItem): void;
}): React.JSX.Element {
  if (props.error) {
    return (
      <div className="feed-thread-note" data-tone="error">
        {props.error}
      </div>
    );
  }
  if (props.comments === null) {
    return <div className="feed-thread-note">Loading comments…</div>;
  }
  if (props.comments.length === 0) {
    return <div className="feed-thread-note">No comments yet.</div>;
  }
  return (
    <ul className="feed-comment-list">
      {props.comments.map((comment) => (
        <li key={comment.id} className="feed-comment">
          <div className="feed-comment-head">
            <b className="feed-comment-by">{comment.displayName}</b>
            <span className="feed-comment-time">
              {timeAgo(comment.createdAt)}
            </span>
            {comment.own ? (
              <button
                type="button"
                className="feed-comment-del"
                aria-label="Delete comment"
                onClick={() => props.onDelete(comment)}
              >
                <SocialIcon name="close" />
              </button>
            ) : null}
          </div>
          <div className="feed-comment-tx">{comment.body}</div>
          {!comment.own ? <CommentSafety comment={comment} /> : null}
        </li>
      ))}
    </ul>
  );
}

function SocialIcon({
  name,
}: {
  name: keyof typeof iconPaths;
}): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={iconPaths[name]} />
    </svg>
  );
}

function messageOf(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "The request could not be completed.";
}
